import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  ArrowLeft, MapPin, Star, CheckCircle, Calendar, Users, Bed, ArrowRight, Flag,
  ArrowSquareOut, Globe,
} from "@phosphor-icons/react";

export default function ResortDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resort, setResort] = useState(null);
  const [busy, setBusy] = useState(false);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultIn = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  }, []);
  const defaultOut = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 18);
    return d.toISOString().slice(0, 10);
  }, []);

  const [checkIn, setCheckIn] = useState(defaultIn);
  const [checkOut, setCheckOut] = useState(defaultOut);
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [roomCat, setRoomCat] = useState("");

  useEffect(() => {
    api.get(`/resorts/${id}`).then((r) => {
      setResort(r.data);
      setRoomCat(r.data.rooms?.[0] || "");
    }).catch(() => toast.error("Resort not found"));
  }, [id]);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const ms = new Date(checkOut) - new Date(checkIn);
    return Math.max(0, Math.round(ms / 86400000));
  }, [checkIn, checkOut]);

  const ROOM_MULT = { "Garden View": 1.0, "Ocean View": 1.25, "Beachfront Suite": 1.7, "Villa": 2.2,
    "Resort Club Room": 1.0, "Beachfront Bure": 1.45, "Club Pool Bure": 1.8, "Presidential Bure": 3.0,
    "Deluxe Room": 1.0, "Pearl Suite": 1.45, "Pool Villa": 2.0,
    "Ocean Bure": 1.0, "Hideaway Bure": 1.35, "Family Bure": 1.6,
    "Ocean Front": 1.0, "Presidential": 2.4,
    "Sunset Pool Bure": 1.25, "Sunset Pool Villa": 1.7 };
  const total = useMemo(() => {
    if (!resort || !nights) return 0;
    return Math.round(resort.nightly_rate_fjd * (ROOM_MULT[roomCat] || 1.0) * nights * rooms);
  }, [resort, nights, rooms, roomCat]);

  const submit = async () => {
    if (!resort || nights <= 0) return toast.error("Pick valid dates");
    setBusy(true);
    try {
      const r = await api.post("/bookings/resort", {
        resort_id: id, check_in: checkIn, check_out: checkOut, guests, rooms, room_category: roomCat,
      });
      toast.success(`Booked ${r.data.resort_name} — FJ$${r.data.total_fjd.toLocaleString()}`);
      navigate("/bookings");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!resort) return <div className="text-sm text-[#0F2537]/60">Loading…</div>;

  return (
    <div className="space-y-6">
      <Link to="/booking?tab=resorts" data-testid="back-to-resorts" className="inline-flex items-center gap-1 text-sm font-bold text-[#0F2537]/70 hover:text-[#0F2537]">
        <ArrowLeft size={14} weight="bold" /> Back to resorts
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl overflow-hidden border border-[#E2E0D8] aspect-[16/9] bg-[#0F2537]/10">
            <img src={resort.image_url} alt={resort.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F2537]/60">
                <MapPin size={12} weight="fill" /> {resort.location}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E05D3A]/10 text-[#E05D3A] text-xs font-bold">
                <Star size={12} weight="fill" /> {resort.rating}
              </span>
              {resort.course_id && (
                <Link to={`/courses/${resort.course_id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0F2537]/5 text-[#0F2537] text-xs font-bold hover:bg-[#0F2537]/10">
                  <Flag size={12} weight="fill" /> On-site golf
                </Link>
              )}
            </div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2">{resort.name}</h1>
            <p className="text-[#0F2537]/80 mt-3 leading-relaxed">{resort.description}</p>
          </div>
          <div>
            <span className="label-eyebrow">Amenities</span>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {resort.amenities.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-sm font-semibold text-[#0F2537]">
                  <CheckCircle size={16} weight="fill" className="text-[#1E5631]" /> {a.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="card-default p-5 space-y-4 h-fit lg:sticky lg:top-24">
          <div>
            <div className="label-eyebrow">From</div>
            <div className="font-heading text-3xl font-black">FJ${resort.nightly_rate_fjd.toLocaleString()}<span className="text-sm font-semibold text-[#0F2537]/60">/night</span></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check-in" icon={Calendar}>
              <input type="date" value={checkIn} min={today} onChange={(e) => setCheckIn(e.target.value)} data-testid="resort-check-in" className="w-full rounded-lg border border-[#E2E0D8] bg-white px-2 py-2 text-sm font-semibold" />
            </Field>
            <Field label="Check-out" icon={Calendar}>
              <input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} data-testid="resort-check-out" className="w-full rounded-lg border border-[#E2E0D8] bg-white px-2 py-2 text-sm font-semibold" />
            </Field>
            <Field label="Guests" icon={Users}>
              <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} data-testid="resort-guests" className="w-full rounded-lg border border-[#E2E0D8] bg-white px-2 py-2 text-sm font-semibold">
                {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <Field label="Rooms" icon={Bed}>
              <select value={rooms} onChange={(e) => setRooms(Number(e.target.value))} data-testid="resort-rooms" className="w-full rounded-lg border border-[#E2E0D8] bg-white px-2 py-2 text-sm font-semibold">
                {[1,2,3,4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Room category" icon={Bed}>
            <select value={roomCat} onChange={(e) => setRoomCat(e.target.value)} data-testid="resort-room-category" className="w-full rounded-lg border border-[#E2E0D8] bg-white px-2 py-2 text-sm font-semibold">
              {resort.rooms.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div className="border-t border-[#E2E0D8] pt-3 space-y-1">
            <Line label="Nights" value={String(nights)} />
            <Line label="Rooms" value={String(rooms)} />
            <div className="flex items-end justify-between pt-2">
              <span className="label-eyebrow">Total</span>
              <span data-testid="resort-total-amount" className="font-heading text-2xl font-black">FJ${total.toLocaleString()}</span>
            </div>
          </div>
          <button
            onClick={submit} disabled={busy || nights <= 0}
            data-testid="confirm-resort-booking-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#E05D3A] text-white text-sm font-bold disabled:opacity-50 hover:brightness-110 transition"
          >
            {busy ? "Booking…" : <>Confirm booking <ArrowRight size={16} weight="bold" /></>}
          </button>
          <p className="text-[10px] text-[#0F2537]/50 leading-relaxed">
            Free cancellation up to 7 days before check-in. Pay at property.
          </p>

          {(resort.booking_url || resort.website_url || resort.expedia_url) && (
            <div className="border-t border-[#E2E0D8] pt-3 space-y-2">
              <div className="label-eyebrow">Or book direct</div>
              {resort.booking_url && (
                <a href={resort.booking_url} target="_blank" rel="noopener noreferrer"
                  data-testid="resort-booking-com-link"
                  className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white text-xs font-bold text-[#0F2537] hover:border-[#003580] hover:text-[#003580] transition">
                  <span className="inline-flex items-center gap-2"><ArrowSquareOut size={14} weight="bold" /> Booking.com</span>
                  <span className="opacity-50">live availability →</span>
                </a>
              )}
              {resort.expedia_url && (
                <a href={resort.expedia_url} target="_blank" rel="noopener noreferrer"
                  data-testid="resort-expedia-link"
                  className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white text-xs font-bold text-[#0F2537] hover:border-[#FFC72C] hover:text-[#FFC72C] transition">
                  <span className="inline-flex items-center gap-2"><ArrowSquareOut size={14} weight="bold" /> Expedia</span>
                  <span className="opacity-50">compare prices →</span>
                </a>
              )}
              {resort.website_url && (
                <a href={resort.website_url} target="_blank" rel="noopener noreferrer"
                  data-testid="resort-website-link"
                  className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#E2E0D8] bg-white text-xs font-bold text-[#0F2537] hover:border-[#0F2537] transition">
                  <span className="inline-flex items-center gap-2"><Globe size={14} weight="bold" /> Resort website</span>
                  <span className="opacity-50">official site →</span>
                </a>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow inline-flex items-center gap-1"><Icon size={11} weight="fill" /> {label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#0F2537]/60">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}
